/**
 * HOA (Homeowners Association) due-diligence checklist + extracted findings,
 * shared between advisor and client.
 *
 * Tier 1 extracts the Demand Statement, Board Minutes, Operating Rules, and
 * the Annual Budget/Reserve Study/Insurance Summary bundle, CC&Rs, and
 * Bylaws. Each family uses a bounded buyer-relevant schema rather than a
 * whole-document summary.
 */

export type ChecklistItemStatus = 'NOT_RECEIVED' | 'RECEIVED';
export type HOAFieldStatus = 'extracted' | 'unconfirmed' | 'not_present';

export type HOADisclosureTier =
  | 'STATUTORY_REQUIRED'
  | 'STATUTORY_CONDITIONAL'
  | 'BUYER_REQUESTED'
  | 'SUPPLEMENTAL';

export interface ChecklistItem {
  id: string;
  label: string;
  status: ChecklistItemStatus;
  documentRecordId?: string;
  sources?: HOAChecklistSource[];
  disclosureTier?: HOADisclosureTier;
  civilCodeReferences?: string[];
  applicabilityNote?: string;
}

export interface HOAChecklistSource {
  documentRecordId: string;
  manifestItemId: string;
  title: string;
  classification?: string;
  representedInChecklist?: boolean;
  primaryPage: number;
  pages: number[];
}

/**
 * California HOA transfer-disclosure taxonomy used wherever the checklist is
 * summarized. New API responses carry `disclosureTier`; this fallback keeps
 * existing processed properties accurately classified until they are
 * reprocessed.
 */
export const HOA_DISCLOSURE_TIER_BY_ITEM_ID: Record<string, HOADisclosureTier> = {
  ccrs: 'STATUTORY_REQUIRED',
  bylaws: 'STATUTORY_REQUIRED',
  'articles-of-incorporation': 'STATUTORY_REQUIRED',
  'rules-regulations': 'STATUTORY_REQUIRED',
  'operating-budget': 'STATUTORY_REQUIRED',
  'reserve-study': 'STATUTORY_REQUIRED',
  'assessment-reserve-disclosure': 'STATUTORY_REQUIRED',
  'financial-statement': 'STATUTORY_REQUIRED',
  'assessment-enforcement-notice': 'STATUTORY_REQUIRED',
  'insurance-summary': 'STATUTORY_REQUIRED',
  'demand-statement': 'STATUTORY_REQUIRED',
  'board-minutes': 'BUYER_REQUESTED',
  'violation-notices': 'STATUTORY_CONDITIONAL',
  'litigation-disclosure': 'SUPPLEMENTAL',
  'environmental-report': 'SUPPLEMENTAL',
};

export function getHOADisclosureTier(item: ChecklistItem): HOADisclosureTier {
  return item.disclosureTier ?? HOA_DISCLOSURE_TIER_BY_ITEM_ID[item.id] ?? 'SUPPLEMENTAL';
}

export interface HOASpecialAssessment {
  description: string;
  amount: number | null;
  status: 'pending' | 'approved' | 'active' | null;
}

export interface HOAViolation {
  description: string;
  status: 'open' | 'resolved' | null;
  noticeDate?: string;
}

export interface HOALitigationMatter {
  description: string;
  status: 'pending' | 'settled' | 'unknown';
}

export interface HOAInsuranceSummary {
  hasMasterPolicy: boolean | null;
  carrier?: string;
  coverageNotes?: string;
  hasEarthquakeInsurance?: boolean | null;
  hasFloodInsurance?: boolean | null;
  /** ISO date the current master policy's stated term ends, if the document
   *  gives one (e.g. a declarations page's policy period). Lets the UI flag
   *  an expired policy period directly, independent of how recently we
   *  processed the document — a freshly reprocessed file can still describe
   *  a policy that already lapsed. */
  policyPeriodEnd?: string;
}

export interface HOAFindings {
  assessmentAmount?: number;
  assessmentFrequency?: 'monthly' | 'quarterly' | 'annually' | null;
  delinquencyStatus?: 'current' | 'delinquent' | 'unknown';
  delinquentAmount?: number | null;
  specialAssessments: HOASpecialAssessment[];
  violations: HOAViolation[];
  litigation: HOALitigationMatter[];
  insurance?: HOAInsuranceSummary;
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
  documentRecordIds?: string[];
}

// ─── Board Minutes — a timeline, not a snapshot ────────────────────────────

export interface HOABoardMeetingEntry {
  date: string;
  heldMeeting: boolean;
  meetingTime?: string;
  adjournedAt?: string;
  directorsPresent?: string[];
  directorsAbsent?: string[];
  vacantSeats?: string[];
  financials?: {
    operatingBalance?: number;
    reserveBalance?: number;
  };
  lienActions?: Array<{ accountRef: string; action: 'authorized' | 'released' }>;
  notableApprovals?: Array<{
    description: string;
    amount?: number;
    fundSource?: 'operating' | 'reserve' | null;
  }>;
  complianceItems?: string[];
  agendaItems?: Array<{
    topic: string;
    discussion?: string;
    outcome?: string;
    actionItem?: string;
    amount?: number;
  }>;
  nextMeeting?: string;
  signedAndApproved: boolean;
  approvedBy?: string;
  approvedDate?: string;
  documentRecordId?: string;
}

export interface HOABoardMinutesFindings {
  meetings: HOABoardMeetingEntry[];
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
}

// ─── Operating Rules — a fixed list of specific fields, not a summary ─────

export type HOACommunityRestrictionCategory =
  | 'leasing'
  | 'occupancy'
  | 'pets'
  | 'parking'
  | 'architecture'
  | 'property-use'
  | 'amenity-access'
  | 'insurance'
  | 'assessments'
  | 'enforcement'
  | 'governance'
  | 'other';

export interface HOACommunityRestriction {
  canonicalKey: string;
  category: HOACommunityRestrictionCategory;
  summary: string;
  thresholdOrLimit?: string | null;
  applicability?: string | null;
  section?: string | null;
  documentRecordIds?: string[];
}

export interface HOAOperatingRulesFindings {
  ageRestrictionPresent?: boolean | null;
  ageRestrictionPresentStatus?: HOAFieldStatus;
  minimumAge?: number | null;
  minimumAgeStatus?: HOAFieldStatus;
  ageRestrictionApplicability?: string | null;
  ageRestrictionApplicabilityStatus?: HOAFieldStatus;
  minimumLeaseTermDays?: number | null;
  smokingProhibited?: boolean | null;
  petLimit?: number | null;
  fineScheduleSummary?: string | null;
  guestParkingLimitHours?: number | null;
  guestParkingMonthlyLimitHours?: number | null;
  lienThresholdAmount?: number | null;
  lienThresholdMonths?: number | null;
  lateChargePercent?: number | null;
  interestRatePercent?: number | null;
  designReviewFee?: number | null;
  vehicleLimit?: number | null;
  occupancyLimitPerBedroom?: number | null;
  occupancyAdditionalPersons?: number | null;
  architecturalApprovalRequired?: boolean | null;
  specialAssessmentPaymentDays?: number | null;
  leaseCopyRequired?: boolean | null;
  garageParkingRequired?: boolean | null;
  notableRestrictionDetails?: HOACommunityRestriction[];
  notableRestrictions?: string[];
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
  documentRecordIds?: string[];
}

export interface HOAGovernanceFindings {
  boardSeatCount?: number | null;
  boardTermLength?: string | null;
  memberMeetingQuorum?: string | null;
  boardMeetingQuorum?: string | null;
  regularMeetingCadence?: string | null;
  assessmentAuthority?: string | null;
  recordsAccess?: string | null;
  ownerApprovalThresholds?: Array<{ action: string; threshold: string }>;
  amendmentThresholds?: Array<{ action: string; threshold: string }>;
  materialGovernanceProvisions?: Array<{
    title: string;
    narrative: string;
    affectedParty?: string | null;
    threshold?: string | null;
    section?: string | null;
    evidenceExcerpt: string;
    confidence: number;
  }>;
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
  documentRecordIds?: string[];
}

export interface HOAArticlesFindings {
  associationName?: string | null;
  incorporationDate?: string | null;
  jurisdiction?: string | null;
  propertyScope?: string | null;
  membershipAppurtenantToOwnership?: boolean | null;
  membershipStructure?: string | null;
  assessmentAuthority?: string | null;
  amendmentThreshold?: string | null;
  dissolutionTerms?: string | null;
  materialCorporatePowers?: Array<{
    category:
      | 'debt-security'
      | 'common-area-transfer'
      | 'merger-annexation'
      | 'assessment'
      | 'membership'
      | 'voting'
      | 'amendment'
      | 'dissolution'
      | 'other';
    title: string;
    narrative: string;
    threshold?: string | null;
    section?: string | null;
    evidenceExcerpt: string;
    confidence: number;
  }>;
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
  documentRecordIds?: string[];
}

// ─── Annual Budget / Reserve Study / Insurance ─────────────────────────────

export interface HOAReserveProjectionYear {
  fiscalYear: string;
  percentFunded: number;
}

export interface HOAReserveCapitalProject {
  name: string;
  plannedYear?: string;
  estimatedCost?: number;
  status?: string;
  fundingSource?: string;
}

export interface HOAFinancialMaterialCallout {
  category:
    | 'assessment'
    | 'reserve'
    | 'capital-project'
    | 'insurance'
    | 'operating-budget'
    | 'other';
  title: string;
  narrative: string;
  amount?: number;
  effectiveDate?: string;
}

export interface HOAAnnualBudgetFindings {
  fiscalPeriod?: string | null;
  monthlyAssessment?: number | null;
  assessmentFrequency?: string | null;
  assessmentIncreaseAmount?: number | null;
  assessmentIncreasePercent?: number | null;
  assessmentEffectiveDate?: string | null;
  totalAnnualIncome?: number | null;
  totalAnnualExpense?: number | null;
  reserveAllocationAnnual?: number | null;
  reserveBalance?: number | null;
  fullyFundedBalance?: number | null;
  reservePercentFunded?: number | null;
  reserveProjection?: HOAReserveProjectionYear[];
  reserveDeficitPerUnit?: number | null;
  specialAssessmentAnticipated?: boolean | null;
  specialAssessmentAmount?: number | null;
  specialAssessmentTiming?: string | null;
  specialAssessmentPurpose?: string | null;
  majorCapitalProjects?: HOAReserveCapitalProject[];
  materialFinancialCallouts?: HOAFinancialMaterialCallout[];
  insurance?: HOAInsuranceSummary;
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
  financialDocumentRecordIds?: string[];
  insuranceDocumentRecordIds?: string[];
}

export interface HOAFinancialStatementFindings {
  documentAvailability?: 'available' | 'unavailable' | 'insufficient' | null;
  associationName?: string | null;
  fiscalPeriod?: string | null;
  reportIssuedDate?: string | null;
  engagementType?: string | null;
  opinionType?: string | null;
  opinionConcern?: string | null;
  totalAssets?: number | null;
  totalLiabilities?: number | null;
  operatingFundBalance?: number | null;
  reserveFundBalance?: number | null;
  operatingSurplusDeficit?: number | null;
  assessmentReceivable?: number | null;
  creditLossAllowance?: number | null;
  monthlyAssessment?: number | null;
  annualAssessmentRevenue?: number | null;
  annualReserveContribution?: number | null;
  reserveStudyDate?: string | null;
  estimatedReplacementCost?: number | null;
  materialCommitments?: Array<{
    category:
      | 'contract'
      | 'capital-project'
      | 'litigation'
      | 'contingency'
      | 'subsequent-event'
      | 'other';
    title: string;
    narrative: string;
    amount?: number | null;
    timing?: string | null;
    evidenceExcerpt: string;
    confidence: number;
  }>;
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
  documentRecordIds?: string[];
}

export type HOAEnvironmentalRiskCategory =
  | 'environmental-covenant'
  | 'environmental-monitoring'
  | 'use-restriction'
  | 'leasing-restriction'
  | 'architectural-control'
  | 'maintenance-obligation'
  | 'easement-access'
  | 'assessment-liability'
  | 'insurance-obligation'
  | 'other';

export interface HOAEnvironmentalEvidence {
  page?: number | null;
  excerpt: string;
}

export interface HOAEnvironmentalRiskFinding {
  category: HOAEnvironmentalRiskCategory;
  severity: 'critical' | 'high' | 'moderate' | 'informational';
  title: string;
  /** One plain sentence, written fresh for callout/headline use — not a
   *  truncation of `summary`. Optional: older extractions predate this
   *  field, callers fall back to truncating `summary` when absent. */
  shortSummary?: string;
  summary: string;
  obligations: string[];
  affectedParties: string[];
  contaminants: string[];
  agencies: string[];
  locations?: string[];
  recommendations?: string[];
  measurements?: Array<{
    contaminant: string;
    value: number;
    unit: string;
    location?: string | null;
    qualifier?: string | null;
  }>;
  permitCompliance?: 'within-limits' | 'exceeds-limits' | 'not-stated' | null;
  recordedReference?: string | null;
  evidence: HOAEnvironmentalEvidence[];
  confidence: number;
  sourceDocument?: string;
  sourceDocumentType?: 'HOA_CCRS' | 'HOA_ENVIRONMENTAL_REPORT';
  documentRecordId?: string;
  documentRecordIds?: string[];
  /** Logical source sections retained from a compiled HOA package. */
  logicalSources?: HOAChecklistSource[];
  sourceDocumentHash?: string;
}

export interface HOAEnvironmentalFindings {
  reviewStatus: 'FLAGGED' | 'NOT_DETECTED' | 'INDETERMINATE';
  risks: HOAEnvironmentalRiskFinding[];
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
}

export interface HOATransferFindings {
  statutoryPackageEstimatedFee?: number | null;
  statutoryPackageEstimatedFeeStatus?: HOAFieldStatus;
  demandDocumentFee?: number | null;
  demandDocumentFeeStatus?: HOAFieldStatus;
  specialAssessmentDocumentFee?: number | null;
  specialAssessmentDocumentFeeStatus?: HOAFieldStatus;
  estoppelInspectionRequired?: boolean | null;
  estoppelInspectionRequiredStatus?: HOAFieldStatus;
  updatedDemandFeeAfterDays?: number | null;
  updatedDemandFeeAfterDaysStatus?: HOAFieldStatus;
  updatedDemandNewOrderAfterDays?: number | null;
  updatedDemandNewOrderAfterDaysStatus?: HOAFieldStatus;
  recordedGrantDeedRequired?: boolean | null;
  recordedGrantDeedRequiredStatus?: HOAFieldStatus;
  extractedAt?: string;
  source?: string;
  documentRecordId?: string;
  documentRecordIds?: string[];
}

export interface HOAChecklistResponse {
  hasHOA: boolean;
  hoaChecklist: ChecklistItem[];
  hoaDocuments?: HOAChecklistSource[];
  hoaFindings?: HOAFindings;
  hoaBoardMinutesFindings?: HOABoardMinutesFindings;
  hoaOperatingRulesFindings?: HOAOperatingRulesFindings;
  hoaGovernanceFindings?: HOAGovernanceFindings;
  hoaArticlesFindings?: HOAArticlesFindings;
  hoaAnnualBudgetFindings?: HOAAnnualBudgetFindings;
  hoaFinancialStatementFindings?: HOAFinancialStatementFindings;
  hoaEnvironmentalFindings?: HOAEnvironmentalFindings;
  hoaTransferFindings?: HOATransferFindings;
}
